import { Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, MaskLines, ClipImage } from "@/components/Motion";
import { CallButton, ConsultButton } from "@/components/Buttons";
import { CTABand } from "@/components/CTABand";
import { LogoMark } from "@/components/Logo";
import { SectionHead, usePageMeta } from "@/components/Common";
import { VALUES, WHY, FOUNDER_PHOTO, CREDENTIALS } from "@/data/site";
import { Testimonials } from "@/components/Testimonials";

const FounderPortrait = () => (
  <div data-testid="founder-portrait" className="relative aspect-[4/5] overflow-hidden rounded-[4px] bg-[#14263D]">
    {FOUNDER_PHOTO ? (
      <img src={FOUNDER_PHOTO} alt="Dwight Saxon, founder of First Due Home Watch & Concierge" className="h-full w-full object-cover" />
    ) : (
      <div className="grain flex h-full flex-col items-center justify-center p-10 text-center text-white">
        <LogoMark className="h-28 w-28" />
        <p className="mt-6 font-display text-3xl">Dwight Saxon</p>
        <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-white/75">Founder &amp; Owner</p>
      </div>
    )}
    <div className="absolute bottom-0 left-0 right-0 flex justify-between bg-[#0e1b2c]/80 px-5 py-3 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-white/85">
      <span>U.S. Army Veteran · Fire Rescue (Ret.), 30 Years</span>
    </div>
  </div>
);

const STORY = [
  "First Due Home Watch & Concierge was founded on a lifetime of dedication to serving and protecting others.",
  "Our founder is a proud United States Army veteran with six years of military service and a retired Palm Beach County Fire Rescue professional with 30 years of fire service experience.",
  "With more than three decades of public service, our founder understands the importance of responsibility, attention to detail, accountability and earning the trust of those who depend on you.",
];

const Story = () => (
  <section data-testid="founder-story" className="bg-[#F8F7F3] py-24 md:py-32">
    <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:px-10 lg:grid-cols-12">
      <Reveal className="lg:col-span-5"><FounderPortrait /></Reveal>
      <div className="space-y-6 text-base leading-relaxed text-[#5F6B76] md:text-lg lg:col-span-6 lg:col-start-7">
        <Reveal>
          <ul data-testid="service-badges" className="flex flex-wrap gap-2">
            {["Veteran-Owned", "Founded by a Retired Fire Rescue Professional"].map((b) => (
              <li key={b} className="rounded-full bg-[#14263D] px-4 py-2 font-mono text-[0.7rem] uppercase tracking-[0.14em] text-white">{b}</li>
            ))}
          </ul>
        </Reveal>
        <SectionHead title={<MaskLines onView lines={["Built on a Lifetime", "of Service"]} />} />
        {STORY.map((p) => <Reveal key={p.slice(0, 24)}><p>{p}</p></Reveal>)}
        <Reveal><p className="font-display text-3xl leading-snug text-[#14263D]">Those same values are the foundation of First Due Home Watch &amp; Concierge.</p></Reveal>
        <Reveal><p>We provide dependable, professional home watch and concierge services to homeowners throughout our Palm Beach County service area, with a commitment to clear communication, detailed property observations and personalized customer service.</p></Reveal>
        <Reveal><p>Whether you're away for a few weeks, spending the season elsewhere or managing a second home, our goal is to help you feel confident that your property is receiving the attention it deserves.</p></Reveal>
        <Reveal className="border-t border-[#14263D]/15 pt-6">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#A52A32]">Veteran-Owned | Founded by a Retired Fire Rescue Professional</p>
          <p className="mt-3 font-display text-2xl italic text-[#14263D]">A Tradition of Service. A Commitment to Your Home.</p>
          <p data-testid="about-affiliation-note" className="mt-4 text-xs leading-relaxed text-[#5F6B76]">First Due Home Watch &amp; Concierge is an independent, privately owned business and is not affiliated with or endorsed by Palm Beach County Fire Rescue or the United States Army.</p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Mission = () => (
  <section data-testid="mission-section" className="grain relative overflow-hidden bg-[#14263D] py-24 text-white md:py-32">
    <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
      <p className="eyebrow !text-[#f0b3b7]">Our Mission</p>
      <Reveal>
        <p className="mt-6 max-w-5xl font-display text-3xl leading-[1.2] md:text-5xl">
          To provide professional, dependable and personalized home watch and concierge services that help homeowners remain informed about their properties while they are away.
        </p>
      </Reveal>
      <div className="mt-20 grid gap-px overflow-hidden rounded-[4px] bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
        {VALUES.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.08} className="bg-[#14263D] p-8 transition-colors duration-500 hover:bg-[#1b3150]">
            <span className="font-mono text-xs text-[#f0b3b7]">0{i + 1}</span>
            <h3 className="mt-6 font-display text-2xl font-semibold">{t}</h3>
            <p className="mt-3 text-sm leading-relaxed text-white/80">{d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyChoose = () => (
  <section data-testid="why-choose-section" className="bg-white py-24 md:py-32">
    <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:px-10 lg:grid-cols-12 lg:items-center">
      <div className="lg:col-span-6">
        <SectionHead eyebrow="Why Choose First Due?" title={<MaskLines onView lines={["Direct. Local.", "Documented."]} />} />
        <ul className="mt-10 grid gap-x-8 sm:grid-cols-2">
          {WHY.map((w, i) => (
            <Reveal as="li" key={w} delay={i * 0.04} className="flex items-start gap-3 border-b border-[#14263D]/10 py-4 text-[#14263D]">
              <Check className="mt-1 h-4 w-4 shrink-0 text-[#A52A32]" /> {w}
            </Reveal>
          ))}
        </ul>
        <Reveal className="mt-12 rounded-[4px] bg-[#F8F7F3] p-8">
          <p className="eyebrow">Our Commitment</p>
          <p className="mt-3 font-display text-3xl leading-snug text-[#14263D]">We treat your home with the care, respect and attention we would expect for our own.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <ConsultButton testId="contact-dwight-btn" label="Contact Dwight" />
            <CallButton testId="about-call-btn" />
          </div>
        </Reveal>
      </div>
      <ClipImage src="/images/mediterranean.jpg" alt="Mediterranean-style South Florida home surrounded by palms" className="h-[420px] rounded-[4px] lg:col-span-5 lg:col-start-8 lg:h-[640px]" />
    </div>
  </section>
);

export default function About() {
  usePageMeta("About Dwight Saxon | Veteran-Owned, Firefighter-Founded Home Watch | First Due", "U.S. Army veteran and retired Palm Beach County Fire Rescue professional with 30 years of service, Dwight Saxon founded First Due Home Watch & Concierge.");
  return (
    <main data-testid="about-page">
      <PageHero testId="about-hero" eyebrow="About First Due" lines={["Meet Our Founder,", "Dwight Saxon."]} img="/images/whitehouse.jpg" alt="White South Florida home among palm trees" />
      <Story />
      <Mission />
      <WhyChoose />
      {CREDENTIALS.length > 0 && <section data-testid="credentials-section" />}
      <Testimonials />
      <CTABand testId="about-cta" />
    </main>
  );
}
